/**
 * Consent Management Composable
 * 
 * Handles user consent for analytics tracking.
 * Works with Google Analytics consent mode v2.
 * 
 * @example
 * ```ts
 * const { hasConsent, grantConsent, revokeConsent } = useConsent()
 * 
 * if (!hasConsent.value) {
 *   // Show consent banner
 * }
 * 
 * grantConsent() // User accepts
 * ```
 */
export const useConsent = () => {
  const config = useRuntimeConfig()
  
  const consentCookie = useCookie<boolean | null>('analytics_consent', {
    default: () => null, // null = no choice made yet, false = rejected, true = accepted
    maxAge: 60 * 60 * 24 * 365, // 1 year
    sameSite: 'lax',
    secure: true
  })
  
  const hasConsent = computed(() => consentCookie.value === true)
  const requireConsent = computed(() => config.public.requireConsent === true)
  
  /**
   * Safe gtag access - only call if gtag is available and consent allows it
   */
  const safeGtag = (command: 'consent', ...args: any[]) => {
    if (!process.client) return
    
    // If consent is rejected, don't call gtag at all
    if (requireConsent.value && consentCookie.value === false) {
      if (import.meta.env.DEV) {
        console.log('🚫 gtag() call blocked in useConsent - consent rejected')
      }
      return
    }
    
    // Only call gtag if it's available (might not be if consent was rejected)
    try {
      const { gtag } = useGtag()
      if (typeof gtag === 'function') {
        // Use type assertion since we know this is a valid gtag call
        ;(gtag as any)(command, ...args)
      }
    } catch (e) {
      // gtag might not be available if consent was rejected and scripts were blocked
      if (import.meta.env.DEV) {
        console.debug('gtag not available (likely blocked due to consent rejection)')
      }
    }
  }
  
  /**
   * Grant consent for analytics tracking
   * Updates Google Analytics consent mode to allow tracking
   */
  const grantConsent = () => {
    if (!process.client) return
    
    consentCookie.value = true
    
    // Update Google Analytics consent mode
    safeGtag('consent', 'update', {
      'analytics_storage': 'granted',
      'ad_storage': 'denied' // We don't use ad storage
    })
    
    if (import.meta.env.DEV) {
      console.log('✅ Analytics consent granted')
    }
  }
  
  /**
   * Revoke consent for analytics tracking
   * Updates Google Analytics consent mode to deny tracking
   */
  const revokeConsent = () => {
    if (!process.client) return
    
    consentCookie.value = false
    
    // Try to update Google Analytics consent mode (if gtag is still available)
    // But don't fail if it's not (scripts might have been blocked)
    safeGtag('consent', 'update', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied'
    })
    
    if (import.meta.env.DEV) {
      console.log('❌ Analytics consent revoked')
    }
  }
  
  return {
    hasConsent,
    requireConsent,
    grantConsent,
    revokeConsent
  }
}

